// Firestore adapter for the Profile Publication module.

import {
  doc,
  getDoc,
  onSnapshot,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";
import { db, isDemoMode } from "../firebase";
import { publishProfile } from "./profilePublication";

export const PUBLIC_PROFILE_COLLECTION = "public_profiles";

function assertUid(uid) {
  if (!uid || typeof uid !== "string") {
    throw new Error("A profile owner UID is required.");
  }
}

function privateProfileRef(uid) {
  return doc(db, "users", uid);
}

function publicProfileRef(uid) {
  return doc(db, PUBLIC_PROFILE_COLLECTION, uid);
}

function phoneIndexRef(phoneNumber) {
  return doc(db, "phone_index", phoneNumber);
}

function cleanUndefined(value) {
  return Object.fromEntries(
    Object.entries(value || {}).filter(([, item]) => item !== undefined)
  );
}

function publicDocument(profile, uid) {
  return {
    ...publishProfile(profile, uid),
    published_at: serverTimestamp(),
  };
}

/** Subscribe to the owner's complete private profile. */
export function subscribePrivateProfile(uid, callback, onError = () => {}) {
  if (isDemoMode || !uid) return () => {};
  return onSnapshot(
    privateProfileRef(uid),
    (snapshot) =>
      callback(snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null),
    onError
  );
}

/**
 * Live subscription to another user's already-sanitized public profile.
 *
 * Rolodex entries are enriched with a one-shot read when they load, which only
 * refreshes when the *owner's* rolodex changes. Subscribing here is what makes
 * someone editing their own profile show up in everyone else's rolodex.
 */
export function subscribePublishedProfile(uid, callback, onError = () => {}) {
  if (!uid || isDemoMode) {
    callback(null);
    return () => {};
  }
  return onSnapshot(
    publicProfileRef(uid),
    (snapshot) =>
      callback(snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null),
    onError
  );
}

/** Read another user's already-sanitized public profile. */
export async function loadPublishedProfile(uid) {
  if (!uid || isDemoMode) return null;
  const snapshot = await getDoc(publicProfileRef(uid));
  return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
}

/**
 * Merge a private profile patch and replace its public projection atomically.
 * Replacing (rather than merging) the projection removes fields immediately
 * when their privacy flag is turned off.
 */
export async function saveProfile(uid, patch) {
  assertUid(uid);
  if (isDemoMode) return null;
  const cleanPatch = cleanUndefined(patch);

  return runTransaction(db, async (transaction) => {
    const privateRef = privateProfileRef(uid);
    const publicRef = publicProfileRef(uid);
    const currentSnapshot = await transaction.get(privateRef);
    const current = currentSnapshot.exists() ? currentSnapshot.data() : {};
    const next = { ...current, ...cleanPatch, uid };

    transaction.set(privateRef, cleanPatch, { merge: true });
    transaction.set(publicRef, publicDocument(next, uid));
    return next;
  });
}

/**
 * Atomically claim a phone trust key and create both profile documents. The
 * caller may safely delete a newly-created Auth account when the claim fails.
 */
export async function registerProfile(uid, phoneNumber, profile) {
  assertUid(uid);
  if (!phoneNumber) throw new Error("A phone number is required.");
  if (isDemoMode) return null;

  return runTransaction(db, async (transaction) => {
    const phoneRef = phoneIndexRef(phoneNumber);
    const existingClaim = await transaction.get(phoneRef);
    if (existingClaim.exists() && existingClaim.data()?.uid !== uid) {
      const error = new Error("An account with this phone number already exists.");
      error.code = "profile/phone-already-in-use";
      throw error;
    }

    const privateProfile = {
      ...cleanUndefined(profile),
      uid,
      phone_number: phoneNumber,
      created_at: serverTimestamp(),
    };
    transaction.set(privateProfileRef(uid), privateProfile);
    transaction.set(publicProfileRef(uid), publicDocument(privateProfile, uid));
    transaction.set(phoneRef, { uid });
    return privateProfile;
  });
}

